import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromStore from '../../../store';
import { UserSubscription } from '../../../user/user-subscription';
import { User } from '../../../user/user';
import { Website } from '../../../websites/website/website';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ApiProStats {
  websitesInfo: {
    total: number;
    real: number;
    percent: number;
  };
  extraWebsitesInfo?: {
    total: number;
    real: number;
    percent: number;
  };
  generatedWebsitesInfo: {
    total: number;
    real: number;
    percent: number;
    extraTotal?: number;
    extraReal?: number;
    extraPercent?: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ApiProService {
  constructor(private _store: Store<fromStore.DbStoreState>) {}

  getApiProStats(user: User, userSubscription: UserSubscription): Observable<ApiProStats> {
    return combineLatest([
      this._store.select(fromStore.getWebsites),
      this._store.select(fromStore.getGeneratedWebsitesCount)
    ]).pipe(
      map(([websites, generations]) => {
        const baseWebsitesCount = user?.hosting_services &&
          parseInt(user.hosting_services.domain_count.parameter_value, 10) + user.additionalWebsiteCount;
        const baseWebsitesCountWithoutAddOn = user?.hosting_services &&
          parseInt(user.hosting_services.domain_count.parameter_value, 10);

        const hostedWebsites = (websites as Website[])
          .filter((website: Website) => !website.parentId && website.isActive &&
            website.type === 'live' && (!!website.domain_hash || website.connectedFrom === 'speed_optimizer'))
          .length;

        const stats: ApiProStats = {
          websitesInfo: {
            total: baseWebsitesCount,
            real: Math.min(hostedWebsites, baseWebsitesCount),
            percent: baseWebsitesCount > 0 ? Math.min(100, Math.round((Math.min(hostedWebsites, baseWebsitesCount) / baseWebsitesCount) * 100)) : 0
          },
          generatedWebsitesInfo: null
        };

        if (userSubscription?.apiResellerPro && userSubscription?.plan?.hasMultiplierService) {
          // Calculate extra websites info
          const multiplier = userSubscription.plan?.multiplierAddOnCount || 0;
          const totalSlots = baseWebsitesCountWithoutAddOn * multiplier;
          const extraSlots = totalSlots - baseWebsitesCountWithoutAddOn;
          const usedExtra = userSubscription.plan?.usedMultiplierAddOnCount || 0;

          stats.extraWebsitesInfo = {
            total: extraSlots,
            real: usedExtra,
            percent: extraSlots > 0 ? Math.min(100, Math.round((usedExtra / extraSlots) * 100)) : 0
          };

          // Calculate generated websites info for API Pro
          const baseGenerationsCount = parseInt(
            userSubscription?.plan?.additionalDomainCount &&
            userSubscription?.plan?.services?.ai_builder.regeneration_additional_count ?
              userSubscription?.plan?.services?.ai_builder.regeneration_additional_count.parameter_value : 0, 10)
            * userSubscription?.plan?.additionalDomainCount;
          const baseGenerationsCountWithoutAddOn = parseInt(userSubscription.plan.services.ai_builder.regeneration_count.parameter_value, 10);
          const regenMultiplier = userSubscription.plan?.multiplierRegenCount;
          const totalRegenSlots = baseGenerationsCountWithoutAddOn * regenMultiplier;
          const extraRegenSlots = totalRegenSlots - baseGenerationsCountWithoutAddOn;
          const usedRegenExtra = generations - (baseGenerationsCount + baseGenerationsCountWithoutAddOn);

          stats.generatedWebsitesInfo = {
            total: baseGenerationsCount + baseGenerationsCountWithoutAddOn,
            real: generations as number,
            percent: ((generations as number) / (baseGenerationsCount + baseGenerationsCountWithoutAddOn)) * 100,
            extraTotal: extraRegenSlots,
            extraReal: usedRegenExtra > 0 ? extraRegenSlots : 0,
            extraPercent: extraRegenSlots > 0 ? Math.min(100, Math.round((usedRegenExtra / extraRegenSlots) * 100)) : 0
          };
        } else if (userSubscription?.plan?.services?.ai_builder) {
          // Calculate generated websites info for non-API Pro
          const regenerationWebsitesCount = parseInt(userSubscription.plan.services.ai_builder.regeneration_count.parameter_value, 10);
          const regenerationAdditionalWebsitesCount = parseInt(
            userSubscription.plan.additionalDomainCount &&
            userSubscription.plan.services.ai_builder.regeneration_additional_count ?
              userSubscription.plan.services.ai_builder.regeneration_additional_count.parameter_value : 0, 10)
            * userSubscription.plan.additionalDomainCount;
          const totalWebsitesCount = regenerationWebsitesCount + regenerationAdditionalWebsitesCount;

          stats.generatedWebsitesInfo = {
            total: totalWebsitesCount,
            real: generations as number,
            percent: ((generations as number) / totalWebsitesCount) * 100
          };
        }

        return stats;
      })
    );
  }
}
